import * as React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import { useInput, useGetMany, useGetList } from 'react-admin'
import { useTranslate } from 'ra-core'

const useStyles = makeStyles(theme => ({
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    backgroundColor: '#fff',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(15,23,42,0.08)',
    },
  },
  drag: { color: '#94a3b8', marginLeft: 8 },
  content: { flex: 1, padding: '12px 0' },
  step: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: '#ccfbf1',
    color: '#0f766e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 13,
    flexShrink: 0,
  },
  actions: { display: 'flex', paddingRight: 4 },
  addRow: { marginTop: theme.spacing(2) },
}))

const SortableQuestionIdsInput = ({ source, country, label }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const locale = country.toLowerCase()
  const { input } = useInput({ source })
  const questionIds = Array.isArray(input.value) ? input.value : []

  const { data: questionsMap } = useGetMany('questions', questionIds)
  const {
    data: availableQuestionsMap,
    ids: availableQuestionIds,
    loading,
  } = useGetList(
    'questions',
    { page: 1, perPage: 200 },
    { field: 'id', order: 'ASC' },
    { country, next_question_id: null },
  )

  const availableQuestions = React.useMemo(() => {
    if (Array.isArray(availableQuestionsMap)) {
      return availableQuestionsMap
    }
    if (availableQuestionIds && availableQuestionsMap) {
      return availableQuestionIds
        .map(id => availableQuestionsMap[id])
        .filter(Boolean)
    }
    return Object.values(availableQuestionsMap || {})
  }, [availableQuestionsMap, availableQuestionIds])

  const move = (index, direction) => {
    const next = [...questionIds]
    const target = index + direction
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    input.onChange(next)
  }

  const remove = index => {
    input.onChange(questionIds.filter((_, i) => i !== index))
  }

  const add = id => {
    if (!id || questionIds.includes(id)) return
    input.onChange([...questionIds, id])
  }

  const getLabel = question => {
    if (!question?.label) return `#${question?.id}`
    return question.label[locale] || question.label.es || `#${question.id}`
  }

  const unusedQuestions = availableQuestions.filter(
    q => !questionIds.includes(q.id),
  )

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
          {label}
        </Typography>
      )}
      {questionIds.length === 0 && (
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {translate('surveys.campaigns.no_questions_in_form')}
        </Typography>
      )}
      {questionIds.map((id, index) => {
        const question = questionsMap && questionsMap[id]
        return (
          <Box key={`${id}-${index}`} className={classes.item}>
            <DragIndicatorIcon className={classes.drag} fontSize="small" />
            <Box className={classes.step}>{index + 1}</Box>
            <Box className={classes.content}>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {question ? getLabel(question) : `ID ${id}`}
              </Typography>
              {question && (
                <Typography variant="caption" color="textSecondary">
                  {question.responseType} · #{question.id}
                </Typography>
              )}
            </Box>
            <Box className={classes.actions}>
              <IconButton
                size="small"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                aria-label="move up"
              >
                <ArrowUpwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => move(index, 1)}
                disabled={index === questionIds.length - 1}
                aria-label="move down"
              >
                <ArrowDownwardIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => remove(index)}
                aria-label="remove"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )
      })}
      <FormControl fullWidth variant="outlined" className={classes.addRow}>
        <InputLabel>{translate('surveys.campaigns.add_question')}</InputLabel>
        <Select
          value=""
          onChange={e => add(e.target.value)}
          label={translate('surveys.campaigns.add_question')}
          disabled={loading || unusedQuestions.length === 0}
        >
          <MenuItem value="">
            <em>
              {unusedQuestions.length === 0
                ? translate('surveys.campaigns.no_more_questions')
                : translate('surveys.campaigns.select_question')}
            </em>
          </MenuItem>
          {unusedQuestions.map(q => (
            <MenuItem key={q.id} value={q.id}>
              {getLabel(q)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  )
}

SortableQuestionIdsInput.propTypes = {
  source: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  label: PropTypes.string,
}

export default SortableQuestionIdsInput
